import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react'
import toast from 'react-hot-toast'
import PropTypes from 'prop-types'
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore'
import { db } from '../firebase'
import { AuthContext } from './AuthContext'
import { useCustomFetch } from '../hooks/useCustomFetch'
import { useNavigate } from 'react-router-dom'
import { getDomain, getPublicStorageUrl } from '../utils'
import { LayoutDashboard, Package, BarChart3, Settings } from 'lucide-react'

export const DashboardContext = createContext()

const RunIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="icon icon-tabler icons-tabler-outline icon-tabler-run"
  >
    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
    <path d="M13 4m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
    <path d="M4 17l5 1l.75 -1.5" />
    <path d="M15 21l0 -4l-4 -3l1 -6" />
    <path d="M7 12l0 -3l5 -1l3 3l3 1" />
  </svg>
)

export const DashboardContextProvider = ({ children }) => {
  const [refreshContext, setRefreshContext] = useState(false)
  const [allProjects, setAllProjects] = useState([])
  const [unsortedProjects, setUnsortedProjects] = useState([])
  const [meetings, setMeetings] = useState([])
  const [products, setProducts] = useState([])
  const [selectedProduct, setSelectedProduct] = useState({})
  const [competitorsLoading, setCompetitorsLoading] = useState(true)
  const [competitors, setCompetitors] = useState([])
  const [allOpportunities, setAllOpportunities] = useState([])
  const [allSessions, setAllSessions] = useState([]) // sessions are meetings with files
  const [sessions, setSessions] = useState([])
  const [sessionsLoading, setSessionsLoading] = useState(true)
  const [meetingsOverview, setMeetingsOverview] = useState({}) // { meetingId: { background: '', validation: {...}, ... } }
  const [filteredProjects, setFilteredProjects] = useState([])
  const [projectsLoading, setProjectsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [workspacesLoading, setWorkspacesLoading] = useState(true)
  const [workspaces, setWorkspaces] = useState({}) // { workspaceId: doc.data() }
  const [activeWorkspace, setActiveWorkspace] = useState({}) // { id: workspaceId, name: workspaceName, sub_id: '',  ... }
  const [meetingJoinPreference, setMeetingJoinPreference] = useState('external')
  const [upcomingMeetings, setUpcomingMeetings] = useState([])
  const [preferencesLoading, setPreferencesLoading] = useState(true)
  const [eventsLoading, setEventsLoading] = useState(true)
  const { user } = useContext(AuthContext)
  const zoomToastIdRef = useRef(null) // Ref to keep track of the zoom toast ID
  const navigate = useNavigate()
  const customFetch = useCustomFetch()
  const [participantsCache, setParticipantsCache] = useState({})
  const [dialogState, setDialogState] = useState({
    isOpen: false,
    type: null, // 'need' | 'feedback' | 'problem' | 'quote'
    noteId: null,
  })

  const showOrUpdateZoomToast = () => {
    if (zoomToastIdRef.current) {
      toast.dismiss(zoomToastIdRef.current) // Dismiss previous toast if it exists
    }
    zoomToastIdRef.current = toast.error(
      () => (
        <div className="flex gap-[10px]">
          <p>Bot cannot join Zoom because you haven't connected your Zoom account.</p>
          <button
            className="border border-[#F9F9F9] rounded px-1"
            style={{ color: '#0052CC' }}
            onClick={() => {
              navigate('/dashboard/integrations')
              clearZoomToast()
            }}
          >
            Connect
          </button>
          <button
            className="border border-[#F9F9F9] rounded px-1"
            style={{ color: '#0052CC' }}
            onClick={clearZoomToast}
          >
            Dismiss
          </button>
        </div>
      ),
      {
        duration: Infinity,
        style: {
          minWidth: '750px',
          paddingTop: '10px',
          paddingBottom: '10px',
          marginTop: '10px',
        },
      }
    )
  }

  const clearZoomToast = () => {
    if (zoomToastIdRef.current) {
      toast.dismiss(zoomToastIdRef.current)
      zoomToastIdRef.current = null
    }
  }

  const isZoomUrl = (meetingUrl) => {
    const zoomURLRegex = /^(https?:\/\/)?(zoom\.us|us\d+web\.zoom\.us)\/j\/\d{9,11}(\/?|\?\S+)$/
    return zoomURLRegex.test(meetingUrl)
  }

  const shouldFireZoomToast = (link) => {
    if (!user.integrations.zoom && isZoomUrl(link)) {
      showOrUpdateZoomToast()
      return true
    }
    return false
  }

  function filterProjects(term) {
    setFilteredProjects(allProjects.filter((project) => project.name.toLowerCase().includes(term.toLowerCase())))
  }

  function cleanMeetingState() {
    setAllSessions([])
    setSessions([])
    setSessionsLoading(false)
    setEventsLoading(false)
  }

  function cleanProjectState() {
    setAllProjects([])
    setFilteredProjects([])
  }

  async function setProjects() {
    setProjectsLoading(true)
    const projectRes = []
    if (user.active_workspace) {
      const q = query(collection(db, 'projects'), where('workspace_id', '==', user.active_workspace))
      const docRefs = await getDocs(q)

      if (!docRefs.empty) {
        docRefs.forEach((project) => {
          projectRes.push({
            id: project.id,
            ...project.data(),
          })
        })

        projectRes.sort((a, b) => (a.created_at - b.created_at > 0 ? -1 : 1))
        setAllProjects(projectRes)
        setFilteredProjects(
          projectRes.filter((project) => project.name.toLowerCase().includes(searchTerm.toLowerCase()))
        )
      } else {
        cleanProjectState()
      }
    }
    setProjectsLoading(false)

    const docRef = doc(db, 'workspaces', user.active_workspace)
    const docSnap = await getDoc(docRef)

    if (docSnap.exists()) {
      setActiveWorkspace({ id: docSnap.id, ...docSnap.data() })
    }
    return projectRes
  }

  async function setInitialCompetitors() {
    setCompetitorsLoading(true)
    if (user.active_workspace) {
      const competitorsRes = await customFetch('/getCompetitors', 'GET')
      setCompetitors(competitorsRes.competitors)
    }
    setCompetitorsLoading(false)
  }

  async function initWorkspaces() {
    setWorkspacesLoading(true)

    const documents = {}
    const workspacesKeys = Object.keys(user.workspaces)
    for (let i = 0; i < workspacesKeys.length; i += 10) {
      const q = query(collection(db, 'workspaces'), where('__name__', 'in', workspacesKeys.slice(i, i + 10)))
      const querySnapshot = await getDocs(q)
      if (!querySnapshot.empty) {
        querySnapshot.forEach((doc) => {
          documents[doc.id] = doc.data()
        })
      } else {
        console.log('No document found with ids:', workspacesKeys.slice(i, i + 10))
      }
    }
    setWorkspaces(documents)
    setWorkspacesLoading(false)
  }

  async function refreshActiveWorkspace() {
    console.log('Refreshing active workspace', user.active_workspace)
    if (!user.active_workspace) return
    const docRef = doc(db, 'workspaces', user.active_workspace)
    const docSnap = await getDoc(docRef)

    if (docSnap.exists()) {
      setActiveWorkspace({ id: docSnap.id, ...docSnap.data() })
    }
  }

  async function initMeetings() {
    setSessionsLoading(true)
    setEventsLoading(true)
    const meetingsRes = await customFetch('/getMeetings', 'GET')
    const newMeets = meetingsRes.meetings
    const joinPreference = meetingsRes.meetingJoinPreference
    if (!Array.isArray(newMeets)) return console.error('newMeets is not an array', newMeets)
    if (newMeets.length === 0) {
      cleanMeetingState()
      return
    }

    // Ensure every meeting has an absolute thumbnail URL (if a thumbnail exists)
    // This is needed so other parts of the app (e.g. the Home feed) can reliably
    // display thumbnails without having to re-compute the public Storage URL.
    for (const meet of newMeets) {
      if (meet.thumbnail && !meet.thumbnail.startsWith('http')) {
        meet.thumbnail = getPublicStorageUrl('thumbs', meet.thumbnail)
      }
    }

    newMeets.sort((a, b) => {
      let timeA = a.created_at
      let timeB = b.created_at
      if (a.start) {
        timeA = new Date(a.start).getTime()
      }
      if (b.start) {
        timeB = new Date(b.start).getTime()
      }
      return timeA < timeB ? 1 : -1
    })
    setMeetings(newMeets)

    // get meetings overview
    const meetingsOverviewRes = await customFetch('/getMeetingOverviews', 'POST', {
      meetingIds: newMeets.map((meet) => meet.id),
    })
    const meetingsOverview = meetingsOverviewRes.meetingsOverview
    setMeetingsOverview(meetingsOverview)

    // Get all projects to create projectId -> projectName mapping
    const projectsMap = {}
    if (user.active_workspace) {
      const projectsQuery = query(collection(db, 'projects'), where('workspace_id', '==', user.active_workspace))
      const projectsSnapshot = await getDocs(projectsQuery)

      projectsSnapshot.forEach((project) => {
        projectsMap[project.id] = project.data().name
      })
    }

    // include meetings with either stored_name or name
    let meetingsWithFiles = newMeets.filter((meet) => meet.stored_name || meet.processing)
    // Only show meetings that have already happened
    meetingsWithFiles = meetingsWithFiles.filter(
      (meeting) => !meeting.start || (meeting.start && new Date(meeting.start).getTime() < Date.now())
    )

    const sessionsInfo = []
    for (const meet of meetingsWithFiles) {
      // set project name if meet has a project_id and it exists in projectsMap
      if (meet.project_id && projectsMap[meet.project_id]) {
        meet.project_name = projectsMap[meet.project_id]
      }
      // set background
      meet.background = meetingsOverview[meet.id]?.background || ''
      sessionsInfo.push(meet)
    }
    setAllSessions(sessionsInfo)
    setSessions(sessionsInfo)
    setSessionsLoading(false)

    const nextMeetings = newMeets
      // Only show future meetings
      .filter((meeting) => {
        const meetingDate = meeting.start ? new Date(meeting.start) : new Date(meeting.created_at)
        return meetingDate > new Date()
      })
      //  Filter events accoridng to preference and attendees
      .filter((meeting) => {
        if (!meeting.attendees?.some((attendee) => attendee.email === user.email)) return false
        if (joinPreference === 'external') {
          const userDomain = getDomain(user.email)
          const attendees = meeting.attendees?.filter((attendee) => !(attendee.email.startsWith('invite+') && attendee.email.endsWith('@usermuse.ai')))
          return attendees.some((attendee) => getDomain(attendee.email) !== userDomain && attendee.email !== user.email)
        } else if (joinPreference === 'every') {
          return true
        } else if (joinPreference === 'created') {
          const organizer = meeting.attendees?.filter(attendee => attendee.organizer)
          if (organizer.length === 0) return false
          return organizer[0].email === user.email
        } else if (joinPreference === 'invited') {
          return meeting.attendees?.some((attendee) => attendee.email.startsWith('invite+') && attendee.email.endsWith('@usermuse.ai'))
        }
      })
      .sort((a, b) => {
        const dateA = a.start ? new Date(a.start) : new Date(a.created_at)
        const dateB = b.start ? new Date(b.start) : new Date(b.created_at)
        return dateA.getTime() - dateB.getTime()
      })
    setUpcomingMeetings(nextMeetings)
    setEventsLoading(false)
  }

  async function setAllProducts() {
    const projects = await setProjects()
    await setInitialCompetitors()
    let productsRes = await customFetch('/getProducts', 'GET')
    productsRes = productsRes.products.map((product) => ({ ...product, projectsData: [] }))

    const unsortedProjects = []
    for (const project of projects) {
      const productIdx = productsRes.findIndex((product) => product.id === project.product)
      if (productIdx !== -1) {
        productsRes[productIdx].projectsData.push(project)
      } else {
        unsortedProjects.push(project)
      }
    }
    productsRes.sort((a, b) => a.order - b.order)
    setProducts(productsRes)
    setUnsortedProjects(unsortedProjects)

    if (productsRes.length) {
      setSelectedProduct(productsRes[0])
      const q = query(
        collection(db, 'opportunities'),
        where(
          'product',
          'in',
          productsRes.map((product) => product.id)
        )
      )
      const querySnapshot = await getDocs(q)
      const opportunitiesRes = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
      setAllOpportunities(opportunitiesRes)
    } else {
      setSelectedProduct({})
      setAllOpportunities([])
    }
  }

  useEffect(() => {
    if (typeof user !== 'object' || Object.keys(user).length === 0) return
    setAllProducts()
    initWorkspaces()
    initMeetings()
  }, [user])

  useEffect(() => {
    if (!refreshContext) return
    setAllProducts()
    initWorkspaces()
    initMeetings()
    setRefreshContext(false)
  }, [refreshContext])

  async function changeProject(meetingId, prevProjectId, projectId) {
    let newProject = {}
    const projectDb = await getDoc(doc(db, 'projects', projectId))
    if (!projectDb.exists()) {
      return console.error('No project to reset with that id!', projectId)
    }
    newProject = { ...projectDb.data(), id: projectId }

    let prevProject = {}
    if (prevProjectId) {
      const projectDb = await getDoc(doc(db, 'projects', prevProjectId))
      if (!projectDb.exists()) {
        return console.error('No previous project to reset with that id!', prevProjectId)
      }
      prevProject = { ...projectDb.data(), id: prevProjectId }
    }

    setAllProjects((prevProjects) =>
      prevProjects.map((project) =>
        project.id === prevProjectId ? prevProject : project.id === projectId ? newProject : project
      )
    )
    setFilteredProjects((prevProjects) =>
      prevProjects.map((project) =>
        project.id === prevProjectId ? prevProject : project.id === projectId ? newProject : project
      )
    )
    setAllSessions((prevSessions) =>
      prevSessions.map((session) =>
        session.id === meetingId ? { ...session, project_id: projectId, project_name: newProject.name } : session
      )
    )
    setSessions((prevSessions) =>
      prevSessions.map((session) =>
        session.id === meetingId ? { ...session, project_id: projectId, project_name: newProject.name } : session
      )
    )
    setMeetings((prevMeetings) =>
      prevMeetings.map((meeting) =>
        meeting.id === meetingId ? { ...meeting, project_id: projectId, project_name: newProject.name } : meeting
      )
    )
    setUpcomingMeetings(prevMeetings => prevMeetings.map((meeting) => meeting.id === meetingId ? { ...meeting, project_id: projectId, project_name: newProject.name } : meeting))
  }

  const deleteProject = async (projectId) => {
    try {
      await customFetch('/deleteProject', 'DELETE', { projectId })

      setAllProjects((prevProjects) => prevProjects.filter((project) => project.id !== projectId))
      setFilteredProjects((prevProjects) => prevProjects.filter((project) => project.id !== projectId))
      setAllSessions((prevSessions) => prevSessions.filter((session) => session.project_id !== projectId))
      setSessions((prevSessions) => prevSessions.filter((session) => session.project_id !== projectId))
      setMeetings((prevMeetings) => prevMeetings.filter((meeting) => meeting.project_id !== projectId))

      setShowDeleteDialog(false)
      toast.success('Project deleted successfully')
    } catch (err) {
      console.error('error deleting project', err)
    }
  }

  async function handleJoinPreference(value) {
    if (!activeWorkspace.id) {
      toast.error('No workspace selected');
      return;
    }
    
    setMeetingJoinPreference(value)
    await customFetch('/updateUserPreferences', 'POST', {
      workspaceId: activeWorkspace.id,
      meetingJoinPreference: value
    })
  }
  
  // Load user preferences when component mounts or workspaceId changes
  useEffect(() => {
    if (!activeWorkspace?.id) return;
    const loadPreferences = async () => {
      setPreferencesLoading(true);
      try {
        const response = await customFetch('/getUserPreferences', 'POST', { 
          workspaceId: activeWorkspace.id
        });

        if (response && response.meetingJoinPreference) {
          setMeetingJoinPreference(response.meetingJoinPreference);
        }
      } catch (error) {
        console.error('Error loading preferences', error);
        toast.error('Failed to load preferences');
      } finally {
        setPreferencesLoading(false);
      }
    }
    
    loadPreferences();
    setInitialCompetitors()
  }, [activeWorkspace?.id]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <LayoutDashboard className="w-5 h-5" /> },
    { id: 'products', label: 'Products', icon: <Package className="w-5 h-5" /> },
    { id: 'competitors', label: 'Competitors', icon: <RunIcon /> },
    { id: 'market', label: 'Market', icon: <BarChart3 className="w-5 h-5" /> },
    { id: 'settings', label: 'Settings', icon: <Settings className="w-5 h-5" /> },
  ]

  async function toggleMeetingRecording(meetingId, shouldRecord) {
    const res = await customFetch('/toggleMeetingRecording', 'PUT', {
      meetingId: meetingId,
      shouldRecord,
    })
    if (res.err) {
      throw new Error(res.err)
    }
    setMeetings((prevMeetings) =>
      prevMeetings.map((meeting) =>
        meeting.id === meetingId ? { ...meeting, should_record: shouldRecord, bot_id: shouldRecord ? meeting.bot_id : null } : meeting
      )
    )
    setSessions((prevSessions) =>
      prevSessions.map((session) =>
        session.id === meetingId ? { ...session, should_record: shouldRecord, bot_id: shouldRecord ? session.bot_id : null } : session
      )
    )
    setAllSessions((prevSessions) =>
      prevSessions.map((session) =>
        session.id === meetingId ? { ...session, should_record: shouldRecord, bot_id: shouldRecord ? session.bot_id : null } : session
      )
    )
  }

  const getParticipant = useCallback(async (participantId) => {
    if (!participantId) return null;
    
    // Check cache first
    if (participantsCache[participantId]) {
      return participantsCache[participantId];
    }

    try {
      const participantDoc = await getDoc(doc(db, 'participants', participantId));
      if (participantDoc.exists()) {
        const participantData = {
          id: participantDoc.id,
          ...participantDoc.data()
        };
        // Update cache
        setParticipantsCache(prev => ({
          ...prev,
          [participantId]: participantData
        }));
        return participantData;
      }
      return null;
    } catch (error) {
      console.error('Error fetching participant:', error);
      return null;
    }
  }, [participantsCache]);

  const openNoteDialog = useCallback((type, noteId) => {
    setDialogState({
      isOpen: true,
      type,
      noteId,
    })
  }, [])

  const closeDialog = useCallback(() => {
    setDialogState({
      isOpen: false,
      type: null,
      noteId: null,
    })
  }, [])

  return (
    <DashboardContext.Provider
      value={{
        filteredProjects,
        setFilteredProjects,
        filterProjects,
        allProjects,
        setAllProjects,
        meetings,
        meetingsOverview,
        searchTerm,
        setSearchTerm,
        projectsLoading,
        workspacesLoading,
        workspaces,
        activeWorkspace,
        setActiveWorkspace,
        refreshActiveWorkspace,
        sessionsLoading,
        sessions,
        setSessions,
        allSessions,
        setAllSessions,
        products,
        setProducts,
        selectedProduct,
        setSelectedProduct,
        unsortedProjects,
        allOpportunities,
        eventsLoading,
        shouldFireZoomToast,
        setProjects,
        setRefreshContext,
        changeProject,
        setAllProducts,
        competitors,
        setCompetitors,
        competitorsLoading,
        tabs,
        deleteProject,
        upcomingMeetings,
        meetingJoinPreference,
        setMeetingJoinPreference,
        handleJoinPreference,
        preferencesLoading,
        setPreferencesLoading,
        toggleMeetingRecording,
        getParticipant,
        dialogState,
        openNoteDialog,
        closeDialog,
        participantsCache,
      }}
    >
      {children}
    </DashboardContext.Provider>
  )
}

DashboardContextProvider.propTypes = {
  children: PropTypes.node,
}
